import { useEffect, useRef, useState } from 'react';
import {
    Box, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { AiService, useAiStatus } from '@services/ai.service';
import MDButton from '@components/MDButton';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { getCrmColors } from '../../crm-theme';
import { LossReason, LossReasonService } from '@layouts/pages/Settings/general/loss-reasons/services/loss-reason.service';

interface Props {
    open: boolean;
    leadName: string;
    /// Nome da etapa de destino; ausente quando a perda não vem de arrastar o card.
    columnName?: string;
    /// Lead com conversa no Multicanal: mostra o botão para a IA sugerir o motivo; nulo esconde.
    leadId?: string | null;
    onCancel: () => void;
    onConfirm: (lossReasonId: string | null, lossNote: string | null) => void;
}

/// Perguntado ao soltar o card: campo para preencher depois nunca é preenchido.
export const CrmLossReasonModal = ({
    open, leadName, columnName, leadId, onCancel, onConfirm,
}: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const [reasons, setReasons] = useState<LossReason[] | null>(null);
    const [chosen, setChosen] = useState<string | null>(null);
    const [note, setNote] = useState('');
    const [attempted, setAttempted] = useState(false);
    const aiConfigured = useAiStatus();
    const [suggesting, setSuggesting] = useState(false);
    const [suggested, setSuggested] = useState(false);
    /// Mexer no motivo ou na nota enquanto a IA lê vale mais que a sugestão que chega depois.
    const touched = useRef(false);
    /// Resposta de um pedido anterior (ou de outra abertura do modal) é descartada.
    const suggestRequest = useRef(0);

    useEffect(() => {
        if (!open) return;

        setChosen(null);
        setNote('');
        setAttempted(false);
        setSuggested(false);
        setSuggesting(false);
        suggestRequest.current += 1;
        touched.current = false;

        new LossReasonService()
            .list()
            // Só os ativos: desativado existe para o histórico, não para ser escolhido.
            .then((res) => setReasons((res.data.data ?? []).filter((m) => m.active)))
            .catch(() => setReasons([]));
    }, [open]);

    /// Cada chamada da IA custa: só roda quando a pessoa pede.
    const suggest = () => {
        if (!leadId || suggesting) return;

        const request = ++suggestRequest.current;
        touched.current = false;
        setSuggesting(true);
        setSuggested(false);

        new AiService()
            .lossReason(leadId)
            .then((res) => {
                const data = res.data.data;
                if (request !== suggestRequest.current || !data?.lossReasonId) return;

                if (touched.current) return;

                setChosen(data.lossReasonId);
                if (data.note) setNote(data.note);
                setSuggested(true);
            })
            // A sugestão é ajuda: falhar não pode travar a perda.
            .catch(() => undefined)
            .finally(() => { if (request === suggestRequest.current) setSuggesting(false); });
    };

    /// Sem motivo ativo cadastrado não há o que escolher: exigir travaria a perda.
    const missingReason = !chosen && (reasons?.length ?? 0) > 0;

    const confirm = () => {
        if (missingReason) {
            setAttempted(true);
            return;
        }

        onConfirm(chosen, note.trim() || null);
    };

    return (
        <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
            <DialogTitle sx={{ pb: 0.5 }}>
                {t('motivosPerda.perguntaTitulo')}
                <Typography variant="caption" sx={{ display: 'block', color: cc.textSecondary }}>
                    {columnName
                        ? t('motivosPerda.perguntaSub', { lead: leadName, etapa: columnName })
                        : leadName}
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'grid', gap: 1.5, pt: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {(reasons ?? []).map((reason) => {
                        const active = chosen === reason.id;

                        return (
                            <Box
                                key={reason.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => { touched.current = true; setChosen(active ? null : reason.id); }}
                                onKeyDown={(e) => {
                                    if (e.key !== 'Enter') return;
                                    touched.current = true;
                                    setChosen(active ? null : reason.id);
                                }}
                                sx={{
                                    px: 1.25,
                                    py: 0.7,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: 12.5,
                                    fontWeight: active ? 700 : 500,
                                    color: active ? cc.onTint : cc.textSecondary,
                                    border: `1px solid ${active ? cc.eventDanger : cc.border}`,
                                    bgcolor: active ? `${cc.eventDanger}1f` : 'transparent',
                                }}
                            >
                                {reason.name}
                            </Box>
                        );
                    })}
                </Box>

                {attempted && missingReason && (
                    <Typography variant="caption" color="error">
                        {t('motivosPerda.escolhaMotivo')}
                    </Typography>
                )}

                {leadId && aiConfigured && !suggesting && !suggested && (
                    <ButtonBase
                        onClick={suggest}
                        sx={{
                            justifySelf: 'start',
                            gap: 0.75,
                            px: 1.25,
                            py: 0.7,
                            borderRadius: '8px',
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: cc.textPrimary,
                            border: `1px solid ${cc.border}`,
                            '&:hover': { bgcolor: cc.countBg },
                        }}
                    >
                        <AutoAwesomeIcon sx={{ fontSize: 15, color: cc.accent }} />
                        {t('ia.sugerirMotivo')}
                    </ButtonBase>
                )}

                {(suggesting || suggested) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {suggesting
                            ? <CircularProgress size={12} sx={{ color: cc.accent }} />
                            : <AutoAwesomeIcon sx={{ fontSize: 14, color: cc.accent }} />}
                        <Typography variant="caption" sx={{ color: cc.textSecondary }}>
                            {suggesting ? t('ia.lendoMotivo') : t('ia.motivoSugerido')}
                        </Typography>
                    </Box>
                )}

                {reasons !== null && reasons.length === 0 && (
                    <Typography variant="caption" sx={{ color: cc.textMuted }}>
                        {t('motivosPerda.nenhumConfigurado')}
                    </Typography>
                )}

                <TextField
                    size="small"
                    multiline
                    minRows={2}
                    value={note}
                    onChange={(e) => { touched.current = true; setNote(e.target.value); }}
                    placeholder={t('motivosPerda.notaPlaceholder')}
                    inputProps={{ maxLength: 500 }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            bgcolor: cc.surfaceBg,
                            borderRadius: '8px',
                            color: cc.textPrimary,
                            '& fieldset': { borderColor: cc.border },
                        },
                        '& .MuiInputBase-input': { color: cc.textPrimary },
                    }}
                />
            </DialogContent>

            <DialogActions>
                <MDButton variant="gradient" color="dark" onClick={onCancel}>
                    {t('general.cancelar')}
                </MDButton>

                <MDButton
                    variant="gradient"
                    color="error"
                    onClick={confirm}
                >
                    {t('motivosPerda.confirmar')}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};
